
import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Recipe } from '@/types';
import { Instagram, Download, MessageCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from '@/components/ui/use-toast';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';

interface RecipeShareImageProps {
  recipe: Recipe;
  onClose?: () => void;
  hideInstructions?: boolean;
}

const RecipeShareImage: React.FC<RecipeShareImageProps> = ({ recipe, onClose, hideInstructions = true }) => {
  const shareRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [proxyImageUrl, setProxyImageUrl] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Pre-cache the image to ensure it's loaded before capture
    if (recipe.imageUrl) {
      setLoadingImage(true);
      
      // Always use the original recipe image URL first
      setProxyImageUrl(recipe.imageUrl);
      
      const img = new Image();
      img.crossOrigin = "anonymous"; 
      img.src = recipe.imageUrl;
      
      img.onload = () => {
        setImageLoaded(true);
        setLoadingImage(false);
      };
      
      img.onerror = () => {
        console.error("Error loading original image, trying proxy:", recipe.imageUrl);
        
        // Fix Imgur URLs if needed
        let modifiedUrl = recipe.imageUrl;
        
        // Make sure imgur URLs have the correct format and extension
        if (modifiedUrl.includes('imgur.com')) {
          // Convert to https://i.imgur.com format
          modifiedUrl = modifiedUrl.replace('imgur.com', 'i.imgur.com');
          
          // Add proper file extension if missing
          if (!modifiedUrl.match(/\.(jpg|jpeg|png|gif)$/i)) {
            modifiedUrl = modifiedUrl + '.jpg';
          }
        }
        
        // Set the modified URL
        setProxyImageUrl(modifiedUrl);
        
        // Try again with the modified URL
        const proxyImg = new Image();
        proxyImg.crossOrigin = "anonymous";
        proxyImg.src = modifiedUrl;
        
        proxyImg.onload = () => {
          setImageLoaded(true);
          setLoadingImage(false);
        };
        
        proxyImg.onerror = () => {
          console.error("Error loading image with modified URL:", modifiedUrl);
          
          // Final fallback - try with a direct proxy service
          // We're much more careful with fallbacks now to avoid using a random image
          const corsProxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(recipe.imageUrl)}`;
          setProxyImageUrl(corsProxyUrl);
          
          const finalAttemptImg = new Image();
          finalAttemptImg.crossOrigin = "anonymous";
          finalAttemptImg.src = corsProxyUrl;
          
          finalAttemptImg.onload = () => {
            setImageLoaded(true);
            setLoadingImage(false);
          };
          
          finalAttemptImg.onerror = () => {
            console.error("All image loading attempts failed:", recipe.imageUrl);
            setImageLoaded(false);
            setLoadingImage(false);
            toast({
              title: "Error",
              description: "No se pudo cargar la imagen de la receta",
              variant: "destructive"
            });
          };
        };
      };
    } else {
      setImageLoaded(true); // No image to load
      setLoadingImage(false);
    }
  }, [recipe.imageUrl]);

  // Function to get a CORS-friendly URL - not used for initial loading anymore
  // but kept as a utility function for later use if needed
  const getCORSProxyUrl = (url: string): string => {
    // First attempt to fix Imgur URLs - add proper file extension
    if (url.includes('imgur.com')) {
      // Make sure the URL has a proper file extension
      if (!url.match(/\.(jpg|jpeg|png|gif)$/i)) {
        url = url + '.jpg'; // Add .jpg extension if missing
      }
      
      // Convert to https://i.imgur.com format which sometimes works better
      url = url.replace('imgur.com', 'i.imgur.com');
    }
    
    return url;
  };

  const captureImage = async () => {
    if (!shareRef.current) {
      toast({
        title: "Error",
        description: "No se pudo generar la imagen",
        variant: "destructive"
      });
      return null;
    }

    if (!imageLoaded) {
      toast({
        title: "Espera un momento",
        description: "La imagen aún se está cargando",
      });
      return null;
    }

    try {
      const canvas = await html2canvas(shareRef.current, {
        backgroundColor: "#FFFFFF", // White background
        scale: 2, // Improve quality for sharing
        useCORS: true, // Allow cross-origin images
        allowTaint: true,
        logging: false, // Reduce console noise
      });
      
      return canvas;
    } catch (error) {
      console.error('Error creating shareable image:', error);
      toast({
        title: "Error",
        description: "Hubo un error al crear la imagen",
        variant: "destructive"
      });
      return null;
    }
  };

  const handleShare = async () => {
    toast({
      description: "Preparando para compartir...",
    });
    
    try {
      // Capture the image for sharing
      const canvas = await captureImage();
      
      // Create a direct link to the recipe
      const recipeLink = `https://guiaveganaapp.com/receta/${recipe.id}`;
      
      // Create the WhatsApp sharing text with the direct recipe link
      let shareText = `¡Mira esta receta vegana de '${recipe.title}' que encontré en Guía Vegana App! 🌱 ${recipeLink}`;
      
      // Create the WhatsApp sharing URL with recipe info
      const text = encodeURIComponent(shareText);
      
      // WhatsApp API doesn't directly support image preview, we need to use web URLs with proper Open Graph tags
      // So we'll just share the text with the direct recipe URL
      const whatsappUrl = `https://wa.me/?text=${text}`;
      
      // Open WhatsApp sharing in a new window/tab
      window.open(whatsappUrl, '_blank');
      
      if (onClose) {
        setTimeout(() => onClose(), 500);
      }
    } catch (error) {
      console.error("Error sharing to WhatsApp:", error);
      toast({
        description: "No se pudo compartir en WhatsApp",
        variant: "destructive"
      });
    }
  };

  const handleDownload = async () => {
    toast({
      description: "Generando imagen...",
    });
    
    try {
      const canvas = await captureImage();
      if (canvas) {
        downloadImage(canvas);
      }
    } catch (error) {
      console.error("Error during image download:", error);
      toast({
        description: "No se pudo generar la imagen",
        variant: "destructive"
      });
    }
  };

  const downloadImage = (canvas: HTMLCanvasElement) => {
    try {
      // Convert canvas to data URL
      const dataUrl = canvas.toDataURL('image/png');
      
      // Create download link
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `receta-${recipe.title.toLowerCase().replace(/\s+/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        description: "¡Imagen guardada!",
      });
      
      if (onClose) onClose();
    } catch (error) {
      console.error('Error downloading image:', error);
      toast({
        description: "No se pudo descargar la imagen",
        variant: "destructive"
      });
    }
  };

  // Get recipe tags if available, otherwise empty array
  const recipeTags = Array.isArray(recipe.tags) 
    ? recipe.tags.filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
    : [];

  // Small function to determine image dimensions based on device
  const getImageContainerStyle = () => {
    if (isMobile) {
      return {
        width: '100%', 
        maxWidth: '350px',
        height: '622px',
        margin: '0 auto',
        overflow: 'hidden'
      };
    }
    
    // Desktop dimensions
    return {
      width: '350px',
      height: '622px',
      maxWidth: '100%',
      margin: '0 auto',
      overflow: 'hidden'
    };
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden w-full flex justify-center">
        {loadingImage && (
          <div className="p-6 bg-gradient-to-b from-vegana-primary/10 to-vegana-secondary/10 rounded-lg w-full max-w-md mx-auto">
            <div className="flex flex-col gap-4">
              <div className="text-center">
                <Skeleton className="h-6 w-3/4 mx-auto" />
              </div>
              <Skeleton className="h-64 w-full rounded-lg" />
              <div className="flex justify-center gap-6">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
              </div>
              <div>
                <Skeleton className="h-5 w-48 mb-2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full mt-1" />
                <Skeleton className="h-4 w-2/3 mt-1" />
              </div>
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-6 w-32 mx-auto" />
            </div>
          </div>
        )}
        
        <div 
          ref={shareRef} 
          className={`p-4 bg-white rounded-lg mx-auto ${loadingImage ? 'hidden' : ''}`}
          style={getImageContainerStyle()}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="text-center mb-2">
              <p className="text-base font-medium">Hoy he probado: <span className="font-bold">'{recipe.title}'</span> 😍</p>
            </div>
            
            {/* Recipe Image - Adjusted height */}
            <div className="relative rounded-lg overflow-hidden mb-3" style={{ height: '33%' }}>
              <img 
                src={proxyImageUrl || recipe.imageUrl} 
                alt={recipe.title} 
                className="w-full h-full object-cover"
                crossOrigin="anonymous"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  console.error("Image failed to load:", e);
                  // Only use placeholder as absolute last resort
                  if (proxyImageUrl?.includes('images.weserv.nl')) {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';
                  } else {
                    // Try a different approach to load the image
                    const corsProxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(recipe.imageUrl)}`;
                    e.currentTarget.src = corsProxyUrl;
                  }
                }}
              />
              <div className="absolute bottom-0 left-0 bg-black/60 text-white px-2 py-1 text-xs text-left">
                Sugerencia de presentación
              </div>
            </div>
            
            {/* Recipe Info */}
            <div className="flex justify-center gap-5 text-xs mb-2">
              <div className="flex items-center">
                <span className="mr-1">⏱</span> {recipe.prepTime + (recipe.cookTime || 0)} min
              </div>
              <div className="flex items-center">
                <span className="mr-1">🍽</span> {recipe.servings} pers.
              </div>
              <div className="flex items-center">
                <span className="mr-1">🔥</span> {recipe.calories} kcal
              </div>
            </div>
            
            {/* Description */}
            <div className="text-xs mb-2">
              <p className="font-semibold mb-1">Sobre esta receta:</p>
              <p className="text-gray-700">{recipe.description || 'Una deliciosa receta vegana para disfrutar en cualquier ocasión.'}</p>
            </div>
            
            {/* Tags */}
            {recipeTags.length > 0 && (
              <div className="mb-2">
                <p className="font-semibold mb-1 text-xs">Etiquetas:</p>
                <div className="flex flex-wrap gap-1">
                  {recipeTags.map((tag, index) => (
                    <span key={index} className="bg-vegana-primary/20 text-vegana-primary px-1.5 py-0.5 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Spacer */}
            <div className="flex-grow"></div>
            
            {/* CTA - Making this more prominent and clickable */}
            <a 
              href={`https://guiaveganaapp.com/receta/${recipe.id}`}
              target="_blank" 
              rel="noopener noreferrer" 
              className="block text-center text-white font-bold p-2 rounded-lg bg-vegana-primary hover:bg-vegana-secondary transition-colors cursor-pointer mb-2"
            >
              <p className="text-xs">¡Descubre +350 recetas veganas y desbloquea tu menú personalizado!</p>
              <p className="text-xs mt-0.5">Toca aquí para ver la receta completa 🌱</p>
            </a>
            
            {/* App logo */}
            <div className="flex justify-center">
              <img
                src="/lovable-uploads/388ffe9d-8dc8-48f3-b959-6a37a52e246f.png"
                alt="Guía Vegana Logo"
                className="h-8"
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="bg-vegana-primary/10 p-4 rounded-lg">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Instagram size={18} /> ¡Tu receta ya está lista para compartir! ✨
          </h3>
          <ol className="list-decimal pl-5 space-y-1 text-sm">
            <li>"Compartir" enviará un mensaje por WhatsApp</li>
            <li>"Guardar imagen" te permite guardar y publicarla en Instagram</li>
            <li>Si etiquetas a @guia_vegana_app te compartimos 💚</li>
          </ol>
        </div>
        
        <div className="flex justify-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleShare} 
            className="flex gap-2 items-center"
            disabled={loadingImage}
          >
            <MessageCircle size={16} /> Compartir
          </Button>
          <Button 
            variant="default" 
            onClick={handleDownload} 
            className="flex gap-2 items-center bg-vegana-primary hover:bg-vegana-secondary"
            disabled={loadingImage || !imageLoaded}
          >
            <Download size={16} /> Guardar imagen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecipeShareImage;
